<template>
  <div v-if="line" class="biz" :style="{ '--accent': line.accent, '--accent2': line.accent2 }">
    <!-- ============ 首屏 ============ -->
    <section class="biz-hero">
      <div class="biz-hero-bg">
        <img :src="img(line.hero)" :alt="`${line.name}解决方案 - 皓辰科技`"/>
        <div class="biz-hero-mask"></div>
        <TechCanvas class="biz-canvas" :color="canvasColor" :link-color="canvasColor"/>
        <div class="grid-bg"></div>
      </div>

      <div class="page biz-hero-inner">
        <nav class="crumb">
          <router-link to="/home">首页</router-link>
          <span>/</span>
          <em>{{ line.name }}</em>
        </nav>

        <span class="eyebrow reveal" v-reveal>{{ line.eyebrow }}</span>
        <h1 class="h1 biz-title reveal" v-reveal="{ delay: 80 }">
          {{ line.name }}
          <span class="biz-tagline">{{ line.tagline }}</span>
        </h1>
        <p class="biz-summary reveal" v-reveal="{ delay: 160 }">{{ line.summary }}</p>

        <div class="biz-actions reveal" v-reveal="{ delay: 240 }">
          <router-link to="/contact" class="btn btn-primary">
            获取{{ line.name }}方案
            <IconSvg name="arrow-right"/>
          </router-link>
          <a class="btn btn-ghost" :href="`tel:${company.phone}`">
            <IconSvg name="phone"/> {{ company.phone }}
          </a>
        </div>

        <ul class="biz-highlights reveal" v-reveal="{ delay: 320 }">
          <li v-for="(c, i) in line.capabilities.slice(0, 3)" :key="i">
            <IconSvg :name="c.icon"/>
            <span>{{ c.title }}</span>
          </li>
        </ul>
      </div>
    </section>

    <!-- ============ 业务概述 + 痛点 ============ -->
    <section class="sec">
      <div class="page">
        <div class="overview">
          <div class="overview-text reveal" v-reveal>
            <span class="eyebrow">Overview</span>
            <h2 class="h2">我们要解决的问题</h2>
            <p class="lead">{{ line.overview }}</p>
          </div>
          <div class="pains">
            <div
              v-for="(p, i) in line.pains"
              :key="i"
              class="pain reveal"
              v-reveal="{ delay: i * 100 }"
            >
              <span class="pain-num">0{{ i + 1 }}</span>
              <h3>{{ p.title }}</h3>
              <p>{{ p.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ 核心能力 ============ -->
    <section class="sec sec-tint">
      <div class="page">
        <SectionHeading
          eyebrow="Capabilities"
          :title="`${line.name}核心能力`"
          desc="模块化交付，可按现状选配：可以先上单点控制，也可以整体规划、分批落地。"
        />

        <div class="cap-grid">
          <div
            v-for="(c, i) in line.capabilities"
            :key="c.title"
            class="cap-card card card-pad reveal"
            v-reveal="{ delay: (i % 3) * 90 }"
          >
            <div class="cap-icon"><IconSvg :name="c.icon"/></div>
            <h3 class="cap-title">{{ c.title }}</h3>
            <p class="cap-desc">{{ c.desc }}</p>
            <div class="cap-tags">
              <span v-for="(t, ti) in c.tags" :key="ti" class="tag">{{ t }}</span>
            </div>
          </div>
        </div>

        <ul v-if="line.roles" class="roles reveal" v-reveal>
          <li class="roles-label">适用对象</li>
          <li v-for="(r, i) in line.roles" :key="i" class="role-chip">{{ r }}</li>
        </ul>
      </div>
    </section>

    <!-- ============ 平台功能矩阵 ============ -->
    <section class="sec">
      <div class="page">
        <SectionHeading
          eyebrow="Modules"
          title="功能拆解到能验收的粒度"
          desc="下面每个模块都是可独立交付、可单独验收的功能集，先上哪些、后上哪些由现场条件决定。"
        />
        <ModuleMatrix :modules="line.modules"/>
      </div>
    </section>

    <!-- ============ 怎么选：三档配置 + 报价口径 ============ -->
    <section class="sec sec-tint">
      <div class="page">
        <SectionHeading
          eyebrow="Scopes"
          :title="`${line.name}怎么选：三档配置`"
          desc="按现状与目标选档位，先小范围验证再扩展。周期为同类项目的经验区间，不是承诺，具体以勘查后的方案排期为准。"
        />
        <PlanTiers :plans="line.plans" :pricing="line.pricing"/>
      </div>
    </section>

    <!-- ============ 技术架构 ============ -->
    <section class="sec">
      <div class="page">
        <SectionHeading
          eyebrow="Architecture"
          title="分层架构，逐层可替换"
          desc="现场设备、边缘网关与平台之间接口清晰，既能兼容存量设备，也方便后续扩展。"
        />
        <div class="arch-layout">
          <ArchDiagram :layers="line.arch"/>
          <div class="arch-side reveal" v-reveal>
            <h3 class="h3">交付形态</h3>
            <ul class="arch-list">
              <li><IconSvg name="check"/><span>现场勘查与点位设计，先确认能落地的范围</span></li>
              <li><IconSvg name="check"/><span>设备接入与协议联调，点表与数据双向核对</span></li>
              <li><IconSvg name="check"/><span>看板、规则与告警配置上线，操作留痕可追溯</span></li>
              <li><IconSvg name="check"/><span>现场培训与文档交付，不依赖原厂支持也能用</span></li>
            </ul>
            <router-link to="/contact" class="btn btn-ghost arch-btn">
              索取方案清单
              <IconSvg name="arrow-right"/>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ 建设成效 ============ -->
    <section class="sec sec-tint">
      <div class="page">
        <SectionHeading
          eyebrow="Outcomes"
          title="上线之后，现场会发生什么变化"
          desc="以下为同类项目的方向性成果，具体数值以现场实测与验收记录为准。"
        />
        <Outcomes
          :items="line.outcomes"
          :image="img(line.outcomeImg)"
          :image-alt="line.name"
          :caption="line.outcomeCaption"
          reverse
        />
      </div>
    </section>

    <!-- ============ 应用场景 ============ -->
    <section class="sec sec-tint">
      <div class="page">
        <SectionHeading
          eyebrow="Scenarios"
          :title="`${line.name}典型场景`"
          desc="以下场景均已具备可交付的实施方案，可按相似度快速裁剪到你的现场。"
        />

        <div class="scene-grid">
          <article
            v-for="(s, i) in line.scenes"
            :key="s.title"
            class="scene-card reveal"
            v-reveal="{ delay: i * 110 }"
          >
            <div class="scene-cover">
              <img :src="img(s.img)" :alt="`${s.title} - ${line.name} | 皓辰科技`"/>
              <span class="scene-shade"></span>
            </div>
            <div class="scene-body">
              <h3>{{ s.title }}</h3>
              <p>{{ s.desc }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- ============ 交付流程 ============ -->
    <section class="sec">
      <div class="page">
        <SectionHeading
          eyebrow="Process"
          title="五步走完一个项目"
          desc="每个阶段都有明确产出物，避免“需求理解偏差”变成项目风险。"
        />

        <div class="flow-cycle reveal" v-reveal>
          <span class="flow-total"><IconSvg name="clock"/> 整体周期约 {{ line.cycle.total }}</span>
          <span class="flow-note">{{ line.cycle.note }}</span>
        </div>

        <ol class="flow">
          <li
            v-for="(f, i) in line.flow"
            :key="f.title"
            class="flow-item reveal"
            v-reveal="{ delay: i * 90 }"
          >
            <span class="flow-index">{{ i + 1 }}</span>
            <div class="flow-body">
              <h3>
                {{ f.title }}
                <em class="flow-dur">{{ line.cycle.stages[i] }}</em>
              </h3>
              <p>{{ f.desc }}</p>
            </div>
            <span v-if="i < line.flow.length - 1" class="flow-line"></span>
          </li>
        </ol>

        <div class="deliver-wrap reveal" v-reveal>
          <h3 class="deliver-title">交付物清单</h3>
          <Deliverables :groups="line.deliverables"/>
        </div>
      </div>
    </section>

    <!-- ============ 在线演示 ============ -->
    <DemoBand
      :accent="line.accent"
      :accent2="line.accent2"
      :bg="line.demoBg || 'gen/demo-preview.jpg'"
    />

    <!-- ============ 常见问题 ============ -->
    <section class="sec">
      <div class="page">
        <SectionHeading
          eyebrow="FAQ"
          :title="`关于${line.name}，客户最常问的 5 个问题`"
          desc="如果这里没有覆盖你的疑问，直接打电话或到访，我们把细节聊清楚。"
        />
        <FaqList :items="line.faq"/>
      </div>
    </section>

    <!-- ============ 其他业务线 ============ -->
    <section class="sec-sm other">
      <div class="page">
        <div class="other-head"><span class="eyebrow">More Business</span></div>
        <div class="other-grid">
          <router-link
            v-for="o in others"
            :key="o.key"
            :to="o.path"
            class="other-card reveal"
            v-reveal
            :style="{ '--accent': o.accent }"
          >
            <h3>{{ o.name }}</h3>
            <p>{{ o.tagline }}</p>
            <span class="other-link">查看业务线 <IconSvg name="arrow-right"/></span>
          </router-link>
        </div>
      </div>
    </section>

    <CtaBand
      :eyebrow="line.en"
      :title="`现在开始推进你的${line.name}项目`"
      desc="把现状讲清楚，皓辰给出可落地的范围、周期与投入建议。"
      :bg="img('gen/cta-bg.jpg')"
    />
  </div>
</template>

<script>
import TechCanvas from '@/components/common/TechCanvas'
import IconSvg from '@/components/common/IconSvg'
import SectionHeading from '@/components/common/SectionHeading'
import ArchDiagram from '@/components/common/ArchDiagram'
import CtaBand from '@/components/common/CtaBand'
import ModuleMatrix from '@/components/common/ModuleMatrix'
import Deliverables from '@/components/common/Deliverables'
import Outcomes from '@/components/common/Outcomes'
import FaqList from '@/components/common/FaqList'
import DemoBand from '@/components/common/DemoBand'
import PlanTiers from '@/components/common/PlanTiers'
import img from '@/utils/assets'
import { businessLines, company } from '@/data/site'

const SITE = 'https://www.hoyotech.top'
const LD_ID = 'ld-service'

export default {
  name: 'BusinessPage',
  components: {
    TechCanvas,
    IconSvg,
    SectionHeading,
    ArchDiagram,
    CtaBand,
    ModuleMatrix,
    Deliverables,
    Outcomes,
    FaqList,
    DemoBand,
    PlanTiers
  },
  props: {
    lineKey: { type: String, required: true }
  },
  data() {
    return { company, img }
  },
  computed: {
    line() {
      return businessLines.find((l) => l.key === this.lineKey) || null
    },
    others() {
      return businessLines.filter((l) => l.key !== this.lineKey)
    },
    canvasColor() {
      const hex = (this.line && this.line.accent) || '#22e0ff'
      const n = parseInt(hex.slice(1), 16)
      return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`
    },
    /** Service + FAQPage + BreadcrumbList：让搜索引擎识别业务线、问答与层级 */
    jsonLd() {
      const l = this.line
      if (!l) return null
      const url = `${SITE}${l.path}`
      const graph = [
        {
          '@type': 'Service',
          '@id': `${url}#service`,
          name: `${l.name}解决方案`,
          serviceType: l.name,
          description: l.summary,
          url,
          provider: { '@type': 'Organization', name: company.name, url: `${SITE}/home` },
          areaServed: { '@type': 'Country', name: '中国' },
          audience: { '@type': 'BusinessAudience', name: (l.roles || []).join('、') },
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: `${l.name}核心能力`,
            itemListElement: (l.capabilities || []).map((c, i) => ({
              '@type': 'Offer',
              position: i + 1,
              name: c.title,
              description: c.desc
            }))
          }
        },
        {
          '@type': 'FAQPage',
          '@id': `${url}#faq`,
          mainEntity: (l.faq || []).map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a }
          }))
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE}/home` },
            { '@type': 'ListItem', position: 2, name: l.name, item: url }
          ]
        }
      ]
      return { '@context': 'https://schema.org', '@graph': graph }
    }
  },
  watch: {
    // 业务线之间切换时组件被复用，必须重新注入，否则会留着上一条线的结构化数据
    lineKey() { this.syncLd() }
  },
  mounted() { this.syncLd() },
  beforeDestroy() {
    const el = document.getElementById(LD_ID)
    if (el) el.remove()
  },
  methods: {
    syncLd() {
      const data = this.jsonLd
      if (!data) return
      let el = document.getElementById(LD_ID)
      if (!el) {
        el = document.createElement('script')
        el.type = 'application/ld+json'
        el.id = LD_ID
        document.head.appendChild(el)
      }
      el.textContent = JSON.stringify(data)
    }
  }
}
</script>

<style lang="scss" scoped>
.biz { position: relative; }

/* ---------------- 首屏 ---------------- */
.biz-hero {
  position: relative;
  min-height: 82vh;
  display: flex;
  align-items: center;
  padding: calc(var(--nav-h) + 46px) 0 86px;
  overflow: hidden;
}

.biz-hero-bg {
  position: absolute;
  inset: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.5;
  }

  .biz-hero-mask {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 66% 60% at 20% 40%, color-mix(in srgb, var(--accent) 22%, transparent), transparent 68%),
      linear-gradient(180deg, rgba(3, 7, 17, 0.88), rgba(3, 7, 17, 0.74) 45%, rgba(3, 7, 17, 0.98));
  }

  .biz-canvas { opacity: 0.6; }
}

.biz-hero-inner { position: relative; z-index: 2; max-width: 900px; }

.crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 26px;
  font-size: 13px;
  color: var(--txt-3);

  a { display: inline-block; padding: 7px 2px; }
  a:hover { color: var(--accent-txt); }
  em { font-style: normal; color: var(--accent-txt); }
}

.biz-title {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  .biz-tagline {
    font-size: clamp(20px, 2.4vw, 32px);
    font-weight: 700;
    background: linear-gradient(100deg, var(--accent), var(--accent2));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.biz-summary {
  margin-top: 24px;
  max-width: 720px;
  font-size: 16.5px;
  line-height: 1.95;
  color: var(--txt-2);
}

.biz-actions {
  display: flex;
  gap: 16px;
  margin-top: 34px;
  flex-wrap: wrap;
}

.biz-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 46px;

  li {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 11px 18px;
    border-radius: 999px;
    border: 1px solid var(--line);
    background: rgba(255, 255, 255, 0.035);
    font-size: 14px;
    color: var(--txt-2);
    backdrop-filter: blur(8px);

    svg { width: 17px; height: 17px; color: var(--accent-txt); }
  }
}

/* ---------------- 概述 + 痛点 ---------------- */
.overview {
  display: grid;
  grid-template-columns: 1fr 1.05fr;
  gap: 56px;
  align-items: start;
}

.overview-text {
  .h2 { margin-top: 16px; }
  .lead { margin-top: 18px; }
}

.pains { display: flex; flex-direction: column; gap: 14px; }

.pain {
  position: relative;
  padding: 24px 26px 24px 76px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: linear-gradient(120deg, rgba(11, 23, 48, 0.66), rgba(5, 12, 27, 0.5));
  transition: transform 0.45s, border-color 0.45s;

  &:hover { transform: translateX(6px); border-color: color-mix(in srgb, var(--accent) 40%, transparent); }

  .pain-num {
    position: absolute;
    left: 26px;
    top: 24px;
    font-size: 15px;
    font-weight: 800;
    color: var(--accent-txt);
    opacity: 0.85;
  }

  h3 { font-size: 17px; color: #fff; }
  p { margin-top: 8px; font-size: 14px; line-height: 1.85; color: var(--txt-3); }
}

/* ---------------- 能力 ---------------- */
.cap-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}

.cap-card {
  display: flex;
  flex-direction: column;

  &:hover .cap-icon { color: var(--accent-txt); border-color: var(--accent); box-shadow: 0 0 26px -8px var(--accent); }
}

.cap-icon {
  width: 50px;
  height: 50px;
  padding: 13px;
  border-radius: 14px;
  border: 1px solid var(--line-strong);
  color: var(--txt-2);
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.4s;
}

.cap-title { margin-top: 20px; font-size: 18px; color: #fff; }
.cap-desc { margin-top: 11px; font-size: 14px; line-height: 1.85; color: var(--txt-3); flex: 1; }

.cap-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(120, 176, 255, 0.1);

  .tag { height: 24px; font-size: 12px; padding: 0 10px; }
}

/* ---------------- 架构 ---------------- */
.arch-layout {
  display: grid;
  grid-template-columns: 1.35fr 0.85fr;
  gap: 40px;
  align-items: start;
}

.arch-side {
  padding: 30px 28px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(13, 27, 54, 0.72), rgba(5, 12, 27, 0.6));

  .h3 { color: #fff; }
}

.arch-list {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  li {
    display: flex;
    gap: 12px;
    font-size: 14px;
    line-height: 1.8;
    color: var(--txt-3);
  }

  svg { width: 18px; height: 18px; flex-shrink: 0; margin-top: 4px; color: var(--accent-txt); }
}

.arch-btn { margin-top: 26px; }

/* ---------------- 场景 ---------------- */
.scene-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}

.scene-card {
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(13, 27, 54, 0.8), rgba(6, 13, 29, 0.8));
  overflow: hidden;
  transition: transform 0.5s, border-color 0.5s, box-shadow 0.5s;

  &:hover { transform: translateY(-8px); border-color: var(--line-strong); box-shadow: var(--shadow-card); }
  &:hover .scene-cover img { transform: scale(1.06); }
}

.scene-cover {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;

  img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.9s cubic-bezier(0.2, 0.7, 0.2, 1); }
  .scene-shade {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(3, 7, 17, 0.2), rgba(6, 13, 29, 0.9));
  }
}

.scene-body {
  padding: 22px 24px 26px;
  h3 { font-size: 18px; color: #fff; }
  p { margin-top: 10px; font-size: 14px; line-height: 1.85; color: var(--txt-3); }
}

/* ---------------- 流程 ---------------- */
.flow-cycle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 18px;
  margin: 0 0 26px;

  .flow-total {
    --ico: 16px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 16px;
    border: 1px solid var(--line-strong);
    border-radius: 999px;
    font-size: 14px;
    font-weight: 600;
    color: var(--txt);

    svg { color: var(--accent-txt); flex-shrink: 0; }
  }

  .flow-note {
    font-size: 13.5px;
    color: var(--txt-3);
  }
}

.flow {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 20px;
}

.flow-dur {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 9px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--accent) 16%, transparent);
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  color: var(--accent-txt);
  vertical-align: 2px;
}

.flow-item {
  position: relative;
  padding: 26px 20px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(11, 23, 48, 0.7), rgba(5, 12, 27, 0.55));
  transition: transform 0.45s, border-color 0.45s;

  &:hover { transform: translateY(-6px); border-color: color-mix(in srgb, var(--accent) 45%, transparent); }
  &:hover .flow-index { color: #01060f; background: var(--accent); }

  .flow-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    font-size: 14px;
    font-weight: 800;
    color: var(--accent-txt);
    border: 1px solid var(--accent);
    transition: all 0.4s;
  }

  .flow-body {
    margin-top: 16px;
    h3 { font-size: 16.5px; color: #fff; }
    p { margin-top: 8px; font-size: 13.5px; line-height: 1.8; color: var(--txt-3); }
  }

  .flow-line {
    position: absolute;
    top: 40px;
    right: -14px;
    width: 26px;
    height: 1px;
    background: linear-gradient(90deg, color-mix(in srgb, var(--accent) 60%, transparent), transparent);
  }
}

/* ---------------- 适用对象 ---------------- */
.roles {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 34px;
  padding-top: 26px;
  border-top: 1px solid rgba(120, 176, 255, 0.12);
}

.roles-label {
  font-size: 13px;
  letter-spacing: 0.2em;
  color: var(--txt-3);
  margin-right: 6px;
}

.role-chip {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 15px;
  border-radius: 999px;
  font-size: 13.5px;
  color: var(--txt-2);
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.035);
  transition: border-color 0.35s, color 0.35s;

  &:hover { border-color: color-mix(in srgb, var(--accent) 50%, transparent); color: #fff; }
}

/* ---------------- 交付物 ---------------- */
.deliver-wrap {
  margin-top: 52px;
  padding-top: 40px;
  border-top: 1px solid rgba(120, 176, 255, 0.12);
}

.deliver-title {
  font-size: 20px;
  color: #fff;
  margin-bottom: 24px;
}

/* ---------------- 其他业务线 ---------------- */
.other-head { margin-bottom: 20px; }
.other-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }

.other-card {
  padding: 28px 30px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(140deg, rgba(12, 26, 52, 0.7), rgba(5, 12, 27, 0.55));
  transition: transform 0.45s, border-color 0.45s, box-shadow 0.45s;

  h3 { font-size: 20px; color: #fff; }
  p { margin-top: 10px; font-size: 14px; color: var(--txt-3); }
  .other-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
    font-size: 14px;
    color: var(--accent-txt);
    svg { width: 17px; height: 17px; transition: transform 0.4s; }
  }

  &:hover { transform: translateY(-6px); border-color: color-mix(in srgb, var(--accent) 45%, transparent); box-shadow: 0 26px 60px -34px var(--accent); }
  &:hover .other-link svg { transform: translateX(5px); }
}

/* ---------------- 响应式 ---------------- */
@media (max-width: 1080px) {
  .overview { grid-template-columns: minmax(0, 1fr); gap: 32px; }
  .cap-grid, .scene-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .flow { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .flow-line { display: none; }
  .arch-layout { grid-template-columns: minmax(0, 1fr); gap: 26px; }
}

@media (max-width: 760px) {
  .biz-hero { min-height: auto; padding: calc(var(--nav-h) + 30px) 0 60px; }
  .biz-summary { font-size: 15px; }
  .biz-highlights { gap: 10px; margin-top: 30px; }
  .biz-highlights li { padding: 9px 14px; font-size: 13px; }
  .cap-grid, .scene-grid, .flow, .other-grid { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  .pain { padding: 20px 20px 20px 62px; }
  .pain .pain-num { left: 20px; top: 20px; }
}
</style>
